import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { analyticsStoreEvent } from "@/mk2/actions";
import {
    CONTESTS_TOPIC_ID,
    COUNSELLING_CATEGORY_ID,
    DAYS_WITH_CATEGORY_ID,
    TESTINGS_TOPIC_ID,
} from "@/mk2/apps/forum/constants";
import { ForumTopics } from "@/mk2/apps/forum/containers/ForumTopics/ForumTopics";
import {
    forumTopicsLoad,
    forumTopicsSearch,
    forumTopicsUpdateSearch,
} from "@/mk2/apps/forum/containers/ForumTopics/ForumTopics.actions";
import forumTopicsPageReducer, {
    ForumTopicsPageState,
    SearchState,
} from "@/mk2/apps/forum/containers/ForumTopics/ForumTopics.reducers";
import forumTopicsSaga from "@/mk2/apps/forum/containers/ForumTopics/ForumTopics.sagas";
import {
    getForumTopicsSearchState,
    getForumTopicsState,
} from "@/mk2/apps/forum/containers/ForumTopics/ForumTopics.selectors";
import { topicUrl } from "@/mk2/apps/forum/helpers";
import { CategoryEntity, CategorySchema, TopicEntity, TopicSchema } from "@/mk2/apps/forum/schemas";
import { breadcrumbsForum } from "@/mk2/apps/forum/structuredData";
import { forumTopicsCategoryUrl, forumTopicsUrl } from "@/mk2/apps/forum/urls";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { UserEntity } from "@/mk2/schemas";
import { findDenormalizedEntityBySlug, getDenormalizedEntities } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_DESCRIPTION_FORUM_category,
    SEO_TITLE_FORUM_TOPICS,
    SEO_TITLE_FORUM_TOPICS_OF_category,
} from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

interface OwnProps {
    // You can change list item to="" link
    itemToTopicUrl(item: TopicEntity);
}

interface StateProps {
    category?: CategoryEntity;
    items: TopicEntity[];
    loadingState: LoadingState;
    errorMessage: string;
    hasNext: number;
    hasPrevious: number;
    queryFromUrl: string;
    queryFromState: string;
    after: number;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;
    baseUrl: string;
}

interface DispatchProps {
    onLoad(username: string, category: string, search: string, after?: number);
    onSearch(
        username: string,
        category: string,
        search: string,
        location: HistoryLocationWithState,
    );
    onUpdateSearch(search: string);
    onStoreEvent(name: string, props: any);
}

type RouteProps = PageRouteProps<{
    categorySlug: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ForumTopicsPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    protected createTopic: boolean = true;

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const categorySlug = ownProps.routeParams.categorySlug;
            const category: CategoryEntity =
                categorySlug === "all"
                    ? null
                    : findDenormalizedEntityBySlug<CategoryEntity>(
                          state,
                          CategorySchema,
                          categorySlug,
                      );
            const parsed = parse(ownProps.location.search, { parseNumbers: true });
            const queryFromUrl = (parsed.search as string) || "";
            const after = parsed.after as number;

            const pageState: ForumTopicsPageState = getForumTopicsState(state);
            const searchState: SearchState = getForumTopicsSearchState(
                state,
                categorySlug,
                queryFromUrl,
            );

            let items;
            let hasNext;
            if (this.createTopic) {
                items = getDenormalizedEntities<TopicEntity>(state, TopicSchema, searchState.ids);
                hasNext = searchState.hasNext;
            } else {
                const itemsAll = getDenormalizedEntities<TopicEntity>(
                    state,
                    TopicSchema,
                    searchState.ids,
                );
                items = itemsAll
                    .filter((item) => item.id !== CONTESTS_TOPIC_ID)
                    .filter((item) => item.id !== TESTINGS_TOPIC_ID)
                    .filter((item) => item.category.id !== COUNSELLING_CATEGORY_ID)
                    .filter((item) => item.category.id !== DAYS_WITH_CATEGORY_ID);

                // when the count of itemsAll is decreased by filtering, the hasNext value is not aligned with
                // the count of items. in these cases we set hasNext to zero
                hasNext = itemsAll.length !== items.length ? 0 : searchState.hasNext;
            }

            const requestUser = getRequestUser(state);
            const requestUserPermissions = getRequestPermissions(state);

            return {
                queryFromState: pageState.search, // Value in the input field
                queryFromUrl, // Value in the URL
                after,
                category,
                errorMessage: searchState.errorMessage,
                loadingState: searchState.loadingState,
                items,
                hasNext,
                hasPrevious: searchState.hasPrevious,
                requestUser,
                requestUserPermissions,
                isMobile: getRequestDeviceMobile(state),
                baseUrl: getRequestBaseUrl(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: forumTopicsLoad,
            onSearch: forumTopicsSearch,
            onUpdateSearch: forumTopicsUpdateSearch,
            onStoreEvent: analyticsStoreEvent,
        };
    }

    public getMetaTitle(props: Props): string {
        const {
            category,
            routeParams: { categorySlug },
        } = props;
        if (categorySlug === "all") {
            return SEO_TITLE_FORUM_TOPICS;
        } else {
            return interpolate(SEO_TITLE_FORUM_TOPICS_OF_category, {
                category: category ? category.name : "...",
            });
        }
    }

    public getMetaDescription(props: Props): string {
        const { category } = props;

        return interpolate(SEO_DESCRIPTION_FORUM_category, {
            category: category ? category.name : "...",
        });
    }

    public getStructuredData(props: Props): object[] {
        const { category, baseUrl } = props;

        if (category) {
            return [breadcrumbsForum(baseUrl, category)];
        }
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        const {
            routeParams: { categorySlug },
            location,
        } = props;
        const qs = parse(location.search);
        return baseUrl + url(forumTopicsCategoryUrl, { categorySlug }, { search: qs.search });
    }

    public render(props: Props): false | JSX.Element {
        return <ForumTopics {...props} itemToTopicUrl={this.itemToTopic} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        const {
            routeParams: { categorySlug },
        } = props;
        return !!categorySlug;
    }

    public getDefaultBackPath(props: Props): string {
        const {
            routeParams: { categorySlug },
        } = props;
        return categorySlug ? forumTopicsUrl : null;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Forum Category - Halfpage Sticky (Desktop)",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: props.isMobile
                ? "Forum Category - Top 01 - Mobile"
                : "Forum Category - Branding (Desktop)",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Forum Category - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { requestUser, isMobile, items } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if ((items?.length || 0) < 10) {
            return null;
        }

        return {
            zone: "Forum Category - Anchor - Mobile",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.forum.forumTopics", forumTopicsPageReducer);
        store.injectSaga("containers.forum.forumTopics", forumTopicsSaga);
    }

    private itemToTopic = (item: TopicEntity) => topicUrl(item);
}
